import { FlowDiagram } from '../../ui/FlowDiagram.js';
import type { FlowStep } from '../../ui/FlowDiagram.js';

/**
 * The Monitor → Analyze → Playbooks → Track loop.
 *
 * Spec 250 (review P2): Kridt's Flow diagram — four equal nodes in one hairline
 * row, the return said in words. It was four cards of different widths with
 * loose arrows. The tour has no "current" stage, so no node is inverted.
 *
 * Spec 256: the loop is also navigation — each node links to its stage's page.
 * `compact` with `current` is the stage loop that sits under the title on those
 * four pages, that page's node inverted in ink.
 */
export type LoopStage = 'monitor' | 'analyze' | 'playbooks' | 'track';

const STEPS: ReadonlyArray<{ stage: LoopStage; name: string; note: string; to: string }> = [
  { stage: 'monitor', name: 'Monitor', note: 'Watch AI answers', to: '/overview' },
  { stage: 'analyze', name: 'Analyze', note: 'Find the gaps', to: '/sources' },
  { stage: 'playbooks', name: 'Playbooks', note: 'Suggest fixes', to: '/playbooks' },
  { stage: 'track', name: 'Track', note: 'Measure impact', to: '/impact' },
];

export interface LoopDiagramProps {
  /** The stage the reader is on. Omitted on the tour. */
  current?: LoopStage;
  /** Names only, no notes or loop line — the form under a page title. */
  compact?: boolean;
  /** False renders plain nodes (the signed-out tour, where the app is behind sign-in). */
  linked?: boolean;
}

export function LoopDiagram({ current, compact = false, linked = true }: LoopDiagramProps) {
  const steps: FlowStep[] = STEPS.map((s) => ({
    name: s.name,
    note: s.note,
    current: s.stage === current,
    to: linked ? s.to : undefined,
  }));
  return (
    <FlowDiagram
      steps={steps}
      label="The GEO Observatory loop"
      loopsTo="Monitor — the loop repeats"
      testId={compact ? 'stage-loop' : 'hiw-loop'}
      compact={compact}
    />
  );
}

/** The compact stage loop under a page title. */
export function StageLoop({ current }: { current: LoopStage }) {
  return <LoopDiagram current={current} compact />;
}
